import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSubscriptions, useUnsubscribe } from '@/hooks';
import { Card, CardContent, Button, Skeleton } from '@/components/ui';
import { formatRelativeTime } from '@/lib/utils';
import { Bell, Eye, Clock, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

const getVisibilityLabel = (visibility: string) => {
  switch (visibility) {
    case 'anonymous':
      return '匿名公开';
    case 'public':
      return '公开';
    case 'editors':
      return '编辑者可见';
    case 'private':
      return '私有';
    default:
      return '公开';
  }
};

export function Subscriptions() {
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useSubscriptions(page, 10);
  const unsubscribe = useUnsubscribe();

  const handleUnsubscribe = (slug: string, title: string) => {
    if (!confirm(`确定要取消订阅 "${title}" 吗？`)) return;

    unsubscribe.mutate(slug, {
      onSuccess: () => toast.success('已取消订阅'),
      onError: () => toast.error('操作失败'),
    });
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto py-12 space-y-6">
        <Skeleton className="h-8 w-32" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <p className="text-red-500">加载失败</p>
      </div>
    );
  }

  const subscriptions = data?.items || [];

  return (
    <div className="max-w-4xl mx-auto py-12 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Bell className="h-6 w-6 text-primary" />
          我的订阅
        </h1>
        <span className="text-sm text-muted-foreground">
          共 {data?.total || 0} 个订阅
        </span>
      </div>

      <p className="text-sm text-muted-foreground">
        订阅的页面更新时，您将收到邮件通知
      </p>

      {subscriptions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Bell className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">暂无订阅的页面</p>
            <Link to="/pages">
              <Button variant="outline">浏览页面</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {subscriptions.map((sub) => (
            <Card key={sub.id}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/pages/${sub.page?.slug}`}
                      className="text-lg font-semibold hover:underline"
                    >
                      {sub.page?.title || '已删除的页面'}
                    </Link>
                    {sub.page && (
                      <>
                        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Eye className="h-3 w-3" />
                            {getVisibilityLabel(sub.page.visibility)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            订阅于 {formatRelativeTime(sub.created_at)}
                          </span>
                        </div>
                        {sub.page.tags && sub.page.tags.length > 0 && (
                          <div className="flex gap-2 mt-2">
                            {sub.page.tags.map((tag) => (
                              <Link
                                key={tag.id}
                                to={`/tags/${tag.name}`}
                                className="px-2 py-0.5 bg-muted rounded text-xs hover:bg-muted/80"
                              >
                                {tag.name}
                              </Link>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-600"
                    onClick={() => handleUnsubscribe(sub.page?.slug || '', sub.page?.title || '')}
                    disabled={unsubscribe.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {data && data.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            上一页
          </Button>
          <span className="py-2 px-4 text-sm text-muted-foreground">
            第 {page} / {data.totalPages} 页
          </span>
          <Button
            variant="outline"
            disabled={page === data.totalPages}
            onClick={() => setPage(page + 1)}
          >
            下一页
          </Button>
        </div>
      )}
    </div>
  );
}
